import { defineStore } from 'pinia'
import AxiosUserService from '../utils/axiosUserService'

const axiosUserService = new AxiosUserService()

let authEpoch = 0
export const currentAuthEpoch = (): number => authEpoch
export function bumpAuthEpoch(): void {
  authEpoch++
}

const extractUsername = (email: string, nickName: string): string => {
  if (!nickName) {
    const parts = email.split("@")
    return parts[0]
  }
  return nickName
}

type UserRole = 'Member' | 'Mentor' | 'Admin' | string
type Language = 'Simplified_Chinese' | 'Spanish' | 'English' | string

export const useUserStore = defineStore('userStore', {
  state: () => ({
    avatar: {} as any,
    avatar_file_id: '',
    nick_name: '',
    country_region: '',
    postal_code: '',
    city_town: '',
    time_flexibility: '',
    location_flexibility: '',
    year_of_teaching_experience: '',
    certificates: '',
    relevant_units: '',
    relevant_subject: '',
    relevant_organization: '',
    educations: '',
    employments: '',
    learning_language: (localStorage.getItem('learningLanguage') || '') as Language,
    site_language: (localStorage.getItem('siteLanguage') || '') as Language,
    skills: '',
    about: '',
    autobiography: '',
    is_paid: false,
    id: '',
    email: '',
    user_role: '' as UserRole,
    is_active: false,
    is_superuser: false,
    is_verified: false,
    verify_status: '',
    can_edit_article: false,
    unread_notifications: 0,
  }),

  actions: {
    // ✅ 給 header / 任意地方切換語言用：單一入口
    setLanguages(learning: Language, site: Language) {
      this.learning_language = learning || ''
      this.site_language = site || ''

      localStorage.setItem('learningLanguage', this.learning_language)
      localStorage.setItem('siteLanguage', this.site_language)
    },

    // Populate the store from an already-fetched /user/me payload, so callers that
    // already have the data (e.g. checkLoginStatus) don't need a second round-trip.
    setUserFromData(data: any) {
        this.avatar = data.avatar || {}
        this.avatar_file_id = data.avatar?.file_id || ''
        this.nick_name = extractUsername(data.email, data.nick_name || '')
        this.country_region = data.country_region || ''
        this.postal_code = data.postal_code || ''
        this.city_town = data.city_town || ''
        this.time_flexibility = data.time_flexibility || ''
        this.location_flexibility = data.location_flexibility || ''
        this.year_of_teaching_experience = data.year_of_teaching_experience || ''
        this.certificates = data.certificates || ''
        this.relevant_units = data.relevant_units || ''
        this.relevant_subject = data.relevant_subject || ''
        this.relevant_organization = data.relevant_organization || ''
        this.educations = data.educations || ''
        this.employments = data.employments || ''

        // ✅ 語言直接進 store，並同步到 localStorage（讓頁面刷新後仍保留）
        this.learning_language = data.learning_language || ''
        this.site_language = data.site_language || ''
        localStorage.setItem('learningLanguage', this.learning_language)
        localStorage.setItem('siteLanguage', this.site_language)

        this.skills = data.skills || ''
        this.about = data.about || ''
        this.autobiography = data.autobiography || ''
        this.is_paid = !!data.is_paid
        this.id = data.id || ''
        this.email = data.email || ''
        this.user_role = data.user_role || ''
        this.is_active = !!data.is_active
        this.is_superuser = !!data.is_superuser
        this.is_verified = !!data.is_verified
        this.verify_status = data.verify_status || ''
        this.can_edit_article = !!data.can_edit_article

        localStorage.setItem('userId', this.id)
        localStorage.setItem('userRole', this.user_role)

        return data
    },

    async fetchUnreadNotifications() {
      // The notification page marks everything seen; a count fetched there could land after it.
      if (location.pathname === '/user/notification') return
      try {
        const res = await axiosUserService.getUnreadNotificationCount(localStorage.token)
        this.unread_notifications = res.data.count
      } catch (error) {
        console.log('Unread notifications error', error)
      }
    },

    async markNotificationsSeen() {
      try {
        await axiosUserService.markNotificationsSeen(localStorage.token)
        this.unread_notifications = 0
      } catch (error) {
        console.log('Mark notifications seen error', error)
      }
    },

    async getUser() {
      const epoch = authEpoch
      try {
        const res = await axiosUserService.getCurrentUser(localStorage.token)
        if (epoch !== authEpoch) return
        return this.setUserFromData(res.data)
      } catch (error) {
        if (epoch !== authEpoch) return
        localStorage.setItem('token', '')
        localStorage.setItem('userId', '')
        localStorage.setItem('isPaid', '')
        localStorage.setItem('userRole', '')
        console.log('Current User error', error)
        throw error
      }
    }
  },

  getters: {
    userData: (state) => state
  }
})
